import React, { useState } from 'react';

import { GridPaginationModel } from '@mui/x-data-grid';

import { useGetTranslationOverviewCatalogLocales } from '~/hooks/apis/customer-communication/useTranslationOverview';
import useDebounced from '~/hooks/useDebounced';

import TranslationOverviewDetailDrawer from './TranslationOverviewDetailDrawer';
import { SEARCH_DEBOUNCE_MS, TRANSLATION_OVERVIEW_DEFAULT_PAGE_SIZE } from './translationOverviewUtils';
import type { TranslationOverviewCatalogRow, TranslationOverviewDetailRow, TranslationOverviewStatus } from './types';

interface Props {
  catalog: TranslationOverviewCatalogRow | null;
  onClose: () => void;
}

/** Drill-in showing every locale of one catalog, paged server-side so catalog width does not matter. */
export default function CatalogLocalesDrawer({ catalog, onClose }: Props) {
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: TRANSLATION_OVERVIEW_DEFAULT_PAGE_SIZE,
  });
  const [searchInput, setSearchInput] = useState('');
  const [status, setStatus] = useState<TranslationOverviewStatus | ''>('');

  const search = useDebounced(searchInput, SEARCH_DEBOUNCE_MS);

  const { data, isFetching, error } = useGetTranslationOverviewCatalogLocales(catalog?.catalogId, {
    page: paginationModel.page,
    pageSize: paginationModel.pageSize,
    search: search.trim() || undefined,
    status: status || undefined,
  });

  const rows: Array<TranslationOverviewDetailRow> = (data?.rows ?? []).map((row) => ({
    id: row.locale,
    label: row.locale,
    status: row.status,
    missingFields: row.missingFields,
    uploadState: row.uploadState,
  }));

  return (
    <TranslationOverviewDetailDrawer
      open={!!catalog}
      title={catalog?.catalogName ?? ''}
      subtitle={catalog ? `${catalog.brand} · ${catalog.totalLocales.toLocaleString()} locales` : undefined}
      rowHeaderName="Locale"
      searchPlaceholder="Search locales"
      rows={rows}
      rowCount={data?.total ?? 0}
      isLoading={isFetching}
      error={error}
      paginationModel={paginationModel}
      onPaginationModelChange={setPaginationModel}
      search={searchInput}
      onSearchChange={(value) => {
        setSearchInput(value);
        setPaginationModel((model) => ({ ...model, page: 0 }));
      }}
      status={status}
      onStatusChange={(value) => {
        setStatus(value);
        setPaginationModel((model) => ({ ...model, page: 0 }));
      }}
      onClose={onClose}
    />
  );
}
